import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';

const api = axios.create({
  baseURL: `${API_BASE_URL}/properties`,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const propertyService = {
  // --- Properties ---
  getAll: async (params = {}) => {
    const response = await api.get('/list/', { params });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/list/${id}/`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/list/', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.patch(`/list/${id}/`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/list/${id}/`);
    return response.data;
  },
  deleteAll: async () => {
    const response = await api.delete('/list/delete_all/');
    return response.data;
  },

  // Bulk CSV import
  bulkImport: async (file, validateOnly = false) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = validateOnly ? '?validate_only=true' : '';
    const response = await api.post(`/bulk-import/${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // --- Appraisals ---
  getAppraisals: async (propertyId = null) => {
    const params = propertyId ? { property: propertyId } : {};
    const response = await api.get('/appraisals/', { params });
    return response.data;
  },
  createAppraisal: async (data) => {
    const response = await api.post('/appraisals/', data);
    return response.data;
  },

  // --- Management ---
  getManagement: async (propertyId = null) => {
    const params = propertyId ? { property: propertyId } : {};
    const response = await api.get('/management/', { params });
    return response.data;
  },
  createManagement: async (data) => {
    const response = await api.post('/management/', data);
    return response.data;
  },

  // --- Images ---
  getImages: async (propertyId = null) => {
    const params = propertyId ? { property: propertyId } : {};
    const response = await api.get('/images/', { params });
    return response.data;
  },
  createImage: async (data) => {
    const response = await api.post('/images/', data);
    return response.data;
  },
  deleteImage: async (id) => {
    const response = await api.delete(`/images/${id}/`);
    return response.data;
  },

  // --- Documents ---
  getDocuments: async (propertyId = null) => {
    const params = propertyId ? { property: propertyId } : {};
    const response = await api.get('/documents/', { params });
    return response.data;
  },
  createDocument: async (data) => {
    const response = await api.post('/documents/', data);
    return response.data;
  },
  deleteDocument: async (id) => {
    const response = await api.delete(`/documents/${id}/`);
    return response.data;
  },

  // --- Landlords ---
  getLandlords: async (propertyId = null) => {
    const params = propertyId ? { property: propertyId } : {};
    const response = await api.get('/landlords/', { params });
    return response.data;
  },
  addLandlord: async (data) => {
    const response = await api.post('/landlords/', data);
    return response.data;
  },
  removeLandlord: async (id) => {
    const response = await api.delete(`/landlords/${id}/`);
    return response.data;
  },
};
